import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { MapPin, Phone, LogIn, User, ArrowLeft, Store } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { api, imageUrl } from "@/lib/api";
import { ProductImage } from "@/components/ProductImage";
import { ProductCard } from "@/components/ProductCard";
import { Reviews, RatingSummary } from "@/components/Reviews";
import { REVIEWS } from "@/constants/testIds";
import { categoryLabel, unitLabel, formatPrice, toNeDigits } from "@/lib/constants";
import { toast } from "sonner";

const ProductDetail = () => {
  const { id } = useParams();
  const { t, lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [seller, setSeller] = useState(null);
  const [contact, setContact] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [loading, setLoading] = useState(true);
  const [revealing, setRevealing] = useState(false);

  useEffect(() => {
    setLoading(true);
    api
      .get(`/products/${id}`)
      .then((res) => {
        setProduct(res.data);
        return api.get(`/sellers/${res.data.seller_id}`);
      })
      .then((res) => setSeller(res.data))
      .catch(() => navigate("/market"))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  const revealPhone = async () => {
    if (!user) {
      toast.info(t("toast.loginRequired"));
      navigate(`/login?redirect=/product/${id}`);
      return;
    }
    setRevealing(true);
    try {
      const { data } = await api.get(`/products/${id}/contact`);
      setContact(data);
    } finally {
      setRevealing(false);
    }
  };

  if (loading || !product) {
    return <div className="max-w-4xl mx-auto px-4 py-10 text-center text-[#6B7280]">{t("empty.loading")}</div>;
  }

  const images = product.images && product.images.length ? product.images : [null];
  const otherProducts = (seller?.products || []).filter((p) => p.id !== product.id);

  return (
    <div className="max-w-4xl mx-auto px-4 pt-6 pb-28 md:pb-8">
      <button data-testid="back-btn" onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-[#6B7280] mb-4 hover:text-[#123A2C]">
        <ArrowLeft className="w-4 h-4" /> {t("nav.market")}
      </button>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Gallery */}
        <div>
          <div className="aspect-square w-full rounded-2xl overflow-hidden bg-[#DCE7E2] border border-[#E5E7EB]">
            <ProductImage images={[images[activeImg]]} seed={product.id} alt={product.title} className="w-full h-full" />
          </div>
          {images.length > 1 && (
            <div className="flex gap-2 mt-3">
              {images.map((img, i) => (
                <button
                  key={i}
                  data-testid={`thumb-${i}`}
                  onClick={() => setActiveImg(i)}
                  className={`w-16 h-16 rounded-lg overflow-hidden border-2 ${activeImg === i ? "border-[#123A2C]" : "border-transparent"}`}
                >
                  <ProductImage images={[img]} seed={product.id + i} alt="" className="w-full h-full" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          <span className="inline-block text-xs font-medium px-2.5 py-1 rounded-full bg-[#DCE7E2] text-[#123A2C]">
            {categoryLabel(product.category, lang)}
          </span>
          <h1 className="text-2xl font-extrabold text-[#14251F] mt-3" data-testid="product-title">{product.title}</h1>

          <div className="mt-3 text-3xl font-extrabold text-[#123A2C]" data-testid="product-price">
            {t("common.rs")} {formatPrice(product.price, lang)}
            <span className="text-base font-normal text-[#9A9E97]"> / {unitLabel(product.unit, lang)}</span>
          </div>

          <a href="#reviews" className="inline-block mt-2" data-testid={REVIEWS.productRating}>
            <RatingSummary average={product.rating_avg} count={product.rating_count} />
          </a>

          <div className="mt-2 text-sm text-[#6B7280]">
            {t("product.quantity")}: <span className="font-medium text-[#14251F]">{toNeDigits(product.quantity, lang)} {unitLabel(product.unit, lang)}</span> {t("product.available")}
          </div>

          <div className="flex items-center gap-1.5 text-[#6B7280] text-sm mt-2">
            <MapPin className="w-4 h-4" /> {product.district}
          </div>

          {/* Contact card */}
          <div className="mt-5 bg-white rounded-2xl border border-[#E5E7EB] p-4">
            <Link to={`/seller/${product.seller_id}`} data-testid="seller-link" className="flex items-center gap-3 group">
              <div className="w-11 h-11 rounded-full bg-[#DCE7E2] flex items-center justify-center">
                <User className="w-5 h-5 text-[#123A2C]" />
              </div>
              <div>
                <div className="font-semibold text-[#14251F] group-hover:text-[#123A2C]">{product.seller_name}</div>
                <div className="text-xs text-[#6B7280]">{product.seller_district}</div>
                {seller?.rating?.count > 0 && (
                  <div className="mt-0.5"><RatingSummary average={seller.rating.average} count={seller.rating.count} /></div>
                )}
              </div>
            </Link>

            {contact ? (
              <a
                data-testid="call-seller-btn"
                href={`tel:${contact.phone}`}
                className="mt-4 w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-[#123A2C] text-white font-semibold hover:bg-[#0D2C21] transition-colors"
              >
                <Phone className="w-5 h-5" /> {t("product.callSeller")} · {contact.phone}
              </a>
            ) : (
              <button
                data-testid="reveal-phone-btn"
                onClick={revealPhone}
                disabled={revealing}
                className="mt-4 w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-[#F5A623] text-[#412402] font-semibold hover:brightness-95 transition disabled:opacity-60"
              >
                {user ? <Phone className="w-5 h-5" /> : <LogIn className="w-5 h-5" />}
                {user ? t("product.revealPhone") : t("product.loginToContact")}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Description */}
      <div className="mt-8">
        <h2 className="text-lg font-bold text-[#14251F] mb-2">{t("product.description")}</h2>
        <p className="text-[#14251F] text-sm leading-relaxed whitespace-pre-wrap" data-testid="product-description">
          {product.description || "—"}
        </p>
      </div>

      <div id="reviews" className="scroll-mt-20">
        <Reviews
          targetType="product"
          targetId={product.id}
          ownerId={product.seller_id}
          reviewerRoles={["buyer"]}
          onChange={({ average, count }) => setProduct((p) => (p ? { ...p, rating_avg: average, rating_count: count } : p))}
        />
      </div>

      {/* Other listings */}
      <div className="mt-10">
        <div className="flex items-center gap-2 mb-4">
          <Store className="w-5 h-5 text-[#123A2C]" />
          <h2 className="text-lg font-bold text-[#14251F]">{t("product.similarNote")}</h2>
        </div>
        {otherProducts.length === 0 ? (
          <p className="text-sm text-[#6B7280]">{t("seller.noOtherListings")}</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5">
            {otherProducts.slice(0, 4).map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProductDetail;
